import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Mic, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, errMsg, BACKEND } from "@/lib/api";
import { PageHead, card, Field, Sel, PatientSelect } from "@/components/admin/kit";
import { CallTypePicker, callBody, KINDS } from "@/components/admin/CallTypePicker";

export function ScriptView({ s, onVoice, busy, onPhone }) {
  const over = s.est_seconds > 60;
  const actual = s.audio_current ? s.audio?.duration : null;
  const pct = Math.min(100, ((actual || s.est_seconds) / 60) * 100);
  return (
    <div className={card} data-testid="script-view">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-[var(--lav-2)] px-2 py-0.5 font-semibold">{s.kind} {s.slot || ""}</span>
        <span className="text-muted-foreground">{s.language} · voice {s.voice} · pace {s.pace}</span>
        <span className={`ml-auto rounded-full px-2 py-0.5 font-bold ${s.audio_current ? "bg-green-100 text-green-800" : s.audio ? "bg-orange-100 text-orange-700" : "bg-gray-100"}`} data-testid="script-audio-status">
          {s.audio_current ? "Voice ready" : s.audio ? "Voice out of date — regenerate" : "No voice yet"}
        </span>
      </div>
      <p className="font-te mt-4 text-lg leading-relaxed text-[var(--ink)]" data-testid="script-text">{s.text}</p>
      <div className="mt-5">
        <div className="flex items-center text-xs"><span>Call length: <b data-testid="script-length">{actual ? `${actual}s (actual)` : `~${s.est_seconds}s (estimate)`}</b></span><span className="ml-auto text-muted-foreground">limit 60s</span></div>
        <div className="mt-1 h-2 overflow-hidden rounded-full bg-[var(--lav-2)]"><div className={`h-full ${over ? "bg-red-600" : "bg-[var(--violet)]"}`} style={{ width: `${pct}%` }} /></div>
        {over && <p className="mt-2 flex items-center gap-1.5 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700" data-testid="script-over-limit"><AlertTriangle size={16} /> Over 60 seconds — remove a medicine from this slot or shorten the templates.</p>}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {onVoice && <Button onClick={onVoice} disabled={busy} data-testid="script-generate-voice-btn">{busy ? <Loader2 className="animate-spin" size={16} /> : <Mic size={16} />} {s.audio_current ? "Voice ready" : "Generate voice"}</Button>}
        {onPhone && <Button variant="outline" onClick={onPhone} disabled={busy} data-testid="script-phone-btn"><Phone size={16} /> Phone quality</Button>}
        {s.audio_current && (
          <>
            <audio controls src={`${BACKEND}/api/audio/${s.id}/full.wav?v=${s.audio.hash}`} className="h-10" data-testid="script-audio-player" />
            {s.phonePlay && <span className="flex items-center gap-2 text-xs font-semibold" data-testid="script-phone-wrap">8 kHz phone<audio controls autoPlay src={`${BACKEND}/api/audio/${s.id}/phone.wav?v=${s.audio.hash}`} className="h-10" data-testid="script-phone-audio" /></span>}
            <a href={`${BACKEND}/api/audio/${s.id}/phone.wav?v=${s.audio.hash}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[var(--violet)] underline" data-testid="script-phone-audio-link">8 kHz phone version</a>
            <span className="text-[11px] text-muted-foreground">{s.audio.loudness}</span>
          </>
        )}
      </div>
    </div>
  );
}

export default function ScriptBuilder() {
  const api = useApi();
  const [q, setQ] = useState({ patient_id: "", kind: "daily", slot: "morning", medicine_id: "", appointment_id: "" });
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const build = async () => {
    const b = callBody(q);
    try { setS((await api.get("/scripts/build", { params: { ...b, slot: b.slot || (q.kind === "stop_confirm" ? q.slot : undefined), medicine_id: b.medicine_id || undefined, appointment_id: b.appointment_id || undefined } })).data); } catch (e) { setS(null); toast.error(errMsg(e)); }
  };
  const voice = async (phonePlay = false) => {
    setBusy(true);
    try { const r = await api.post(`/scripts/${s.id}/voice`); setS({ ...s, audio: r.data.audio, audio_current: true, phonePlay: phonePlay === true || s.phonePlay }); if (phonePlay !== true) toast.success(r.data.reused ? "Voice unchanged — reused stored audio" : "Voice generated"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="script-builder-page">
      <PageHead title="Script builder" sub="All active medicines from all hospitals merge into one call per time slot. Voice is generated once and reused until something changes." />
      <div className={`${card} grid gap-3 md:grid-cols-5`}>
        <Field label="Patient"><PatientSelect value={q.patient_id} onChange={(v) => setQ({ ...q, patient_id: v })} testid="script-patient-select" /></Field>
        {q.kind === "stop_confirm"
          ? <Field label="Call type"><Sel value={q.kind} onChange={(v) => setQ({ ...q, kind: v })} options={[...KINDS, ["stop_confirm", "Stop confirmation (after 9)"]]} testid="script-kind-select" /></Field>
          : <CallTypePicker q={q} setQ={setQ} idp="script" />}
        {q.kind !== "stop_confirm" && <button type="button" className="self-end pb-2 text-left text-xs font-semibold text-[var(--violet)] underline" onClick={() => setQ({ ...q, kind: "stop_confirm" })} data-testid="script-stop-confirm-link">Stop confirmation (after 9)</button>}
        <div className="flex items-end"><Button className="w-full" disabled={!q.patient_id} onClick={build} data-testid="script-build-btn">Build script</Button></div>
      </div>
      <div className="mt-4">{s && <ScriptView s={s} onVoice={voice} onPhone={() => voice(true)} busy={busy} />}</div>
    </div>
  );
}
